/**
 * Typed loader for the NAPI-RS native addon.
 *
 * The addon's platform-resolution glue is generated at the package root as
 * CommonJS (`../index.js`, see `napi build`). We load it once here via
 * `createRequire` so the rest of the surface gets a typed handle. tsup keeps
 * `../index.js` out of the bundle (see tsup.config.ts) so the require survives
 * to runtime, resolving relative to the compiled file in `dist/`.
 */
import { createRequire } from "node:module";

import type {
  AgentAuth,
  EncryptedMemory,
  KeyPair,
  MemoryDiffResult,
  MemoryEntry,
  MemorySnapshot,
  RedactResult,
} from "./types.js";

/** Raw shape produced by `native.buildScanRequest` — the JS side mirrors
 * this in a friendlier `MemoryScanRequest` type once the wrappers land. */
export interface ScanRequestRaw {
  prompt: string;
  context: string;
  redaction: RedactResult;
  has_evasion: boolean;
}

/** Result of parsing a `SCORE: N — rest` prefix from an LLM reason. */
export interface ParsedScoreRaw {
  score: number | null;
  clean_reason: string;
}

/** `{ok, reason?}` outcome from `verifyJudgeResponseSignature`. */
export interface JudgeSignatureCheckRaw {
  ok: boolean;
  reason?: string;
}

/** Op-arg struct for `add_agent_memory` — bytes come back as Buffer. */
export interface AddAgentMemoryArgsRaw {
  agent_pubkey: Buffer;
  content_cipher: Buffer;
  nonce: Buffer;
  score: number;
}

export interface RollbackAgentMemoryArgsRaw {
  agent_pubkey: Buffer;
  to_id: number;
  reason: string;
}

export interface AgentPubkeyOnlyQueryRaw {
  agent_pubkey_hex: string;
}

export interface MemoryByIdQueryRaw {
  agent_pubkey_hex: string;
  id: number;
}

export interface RawMemoryRowStruct {
  id: number;
  content_cipher: Buffer;
  nonce: Buffer;
  score: number;
  is_active: boolean;
  created_at: number;
  updated_at: number;
}

export interface RawRollbackRowStruct {
  from_id: number;
  to_id: number;
  reason: string;
  signer: Buffer;
  created_at: number;
}

export interface EncryptedToolCallArgsRaw {
  tool_call_id: string;
  tool_name: string;
  content_cipher: Buffer;
  action_hash: string;
  encryption_key_ref: Buffer;
  claim_hash: string;
}

/** Shape of the NAPI-RS addon (mirrors index.d.ts, typed against our structs). */
export interface NativeBindings {
  isValidPrivateKey(s: string): boolean;
  derivePublicKey(s: string): string;
  generateKeypair(): KeyPair;
  loadAgent(privkey: string): AgentAuth;
  signLogToolCall(
    toolCallId: string,
    action: string,
    context: string,
    toolName: string,
    toolArgsJson: string,
    privkey: string,
    blockchainRid: string,
  ): string;
  signJudgeAction(
    judgmentId: string,
    action: string,
    context: string,
    extra: string,
    privkey: string,
    blockchainRid: string,
  ): string;
  verifySignature(
    body: Buffer,
    signatureHex: string,
    pubkeyHex: string,
  ): boolean;
  normalizeForMatching(s: string): string;
  containsEvasionCharacters(s: string): boolean;
  redactSecrets(s: string): RedactResult;
  containsSecret(s: string): boolean;
  createMemorySnapshot(entries: MemoryEntry[], takenAt: number): MemorySnapshot;
  diffMemorySnapshots(
    before: MemorySnapshot,
    after: MemorySnapshot,
  ): MemoryDiffResult;
  deriveMemoryKey(privkeyHex: string): Buffer;
  encryptMemoryContent(plaintext: string, key: Buffer): EncryptedMemory;
  decryptMemoryContent(
    ciphertext: Buffer,
    nonce: Buffer,
    key: Buffer,
  ): string;
  DEFAULT_BLOCKCHAIN_RID: string;
  DEFAULT_PRIVATE_BLOCKCHAIN_RID: string;
  DEFAULT_ENDPOINT: string;
  HONEYCOMB_KEY: string;
  defaultChromiaNodeUrls(): string[];
  defaultPrivateNodeUrls(): string[];

  classifyMemoryWrite(
    event: unknown,
    ctx: unknown,
    opts?: { patterns?: string[]; toolNames?: string[] },
  ): MemoryEntry | null;
  classifyMemoryRead(
    event: unknown,
    ctx: unknown,
    opts?: {
      readToolNames?: string[];
      patterns?: string[];
      genericReadToolNames?: string[];
    },
  ): boolean;
  defaultMemoryWriteToolNames(): string[];
  defaultMemoryPathPatterns(): string[];
  defaultMemoryReadToolNames(): string[];

  memoryScanContext(): string;
  buildScanRequest(entry: MemoryEntry): ScanRequestRaw;
  formatEntryForScan(entry: MemoryEntry, hasEvasion: boolean): string;
  mapVerdict(actionType: string, confidence: number, threshold: number): string;
  parseScoreFromReason(reason: string): ParsedScoreRaw;
  defaultScoreForVerdict(verdict: string): number;

  encryptForOrg(
    plaintext: string,
    orgPubKeyHex: string,
    aad: string,
    domain?: string,
  ): Buffer;
  decryptForOrg(
    payload: Buffer,
    orgPrivKeyHex: string,
    aad: string,
    domain?: string,
  ): string;
  encryptedLength(plaintextByteLength: number): number;
  ECIES_FORMAT_VERSION: number;

  verifyJudgeResponseSignature(
    body: Buffer,
    signatureHex: string | null,
    pubkeyHex: string,
  ): JudgeSignatureCheckRaw;

  buildAddAgentMemoryArgs(
    agentPubkeyHex: string,
    contentCipher: Buffer,
    nonce: Buffer,
    score: number,
  ): AddAgentMemoryArgsRaw;
  buildRollbackAgentMemoryArgs(
    agentPubkeyHex: string,
    toId: number,
    reason: string,
  ): RollbackAgentMemoryArgsRaw;
  validateScore(score: number): void;
  validateAgentPubkeyHex(pubkeyHex: string): Buffer;
  validateMemoryId(id: number): void;
  validateRollbackReason(reason: string): void;
  validateMemoryNonce(nonce: Buffer): void;

  buildGetActiveMemoryIdQuery(agentPubkeyHex: string): AgentPubkeyOnlyQueryRaw;
  buildGetAgentMemoryQuery(agentPubkeyHex: string): AgentPubkeyOnlyQueryRaw;
  buildGetAllAgentMemoryQuery(agentPubkeyHex: string): AgentPubkeyOnlyQueryRaw;
  buildGetAgentMemoryHistoryQuery(
    agentPubkeyHex: string,
  ): AgentPubkeyOnlyQueryRaw;
  buildGetAgentMemoryRollbackHistoryQuery(
    agentPubkeyHex: string,
  ): AgentPubkeyOnlyQueryRaw;
  buildGetAgentMemoryByIdQuery(
    agentPubkeyHex: string,
    id: number,
  ): MemoryByIdQueryRaw;

  parseByteArrayField(value: unknown): Buffer;
  parseMemoryRow(value: unknown): RawMemoryRowStruct;
  parseMemoryRows(value: unknown): RawMemoryRowStruct[];
  parseRollbackRow(value: unknown): RawRollbackRowStruct;
  parseRollbackRows(value: unknown): RawRollbackRowStruct[];
  parseActiveMemoryId(value: unknown): number | null;

  normalizeActionForHash(action: string): string;
  computeActionHash(action: string): string;
  claimHashHex(
    toolName: string,
    action: string,
    context: string,
    toolArgsJson: string,
  ): string;
  buildEncryptedToolcallArgs(
    toolCallId: string,
    action: string,
    context: string,
    toolName: string,
    toolArgsJson: string,
    orgEncryptionPubKeyHex: string,
  ): EncryptedToolCallArgsRaw;
  signEncryptedToolCall(
    toolCallId: string,
    action: string,
    context: string,
    toolName: string,
    toolArgsJson: string,
    orgEncryptionPubKeyHex: string,
    privkeyHex: string,
    blockchainRidHex: string,
  ): string;

  OP_ADD_AGENT_MEMORY: string;
  OP_ROLLBACK_AGENT_MEMORY: string;
  OP_LOG_ENCRYPTED_TOOL_CALL: string;
  QUERY_GET_ACTIVE_MEMORY_ID: string;
  QUERY_GET_AGENT_MEMORY: string;
  QUERY_GET_ALL_AGENT_MEMORY: string;
  QUERY_GET_AGENT_MEMORY_HISTORY: string;
  QUERY_GET_AGENT_MEMORY_BY_ID: string;
  QUERY_GET_AGENT_MEMORY_ROLLBACK_HISTORY: string;
  ARG_AGENT_PUBKEY: string;
  ARG_ID: string;
}

// tsup emits both ESM (dist/index.mjs) and CJS (dist/index.js). In the CJS
// bundle `import.meta.url` is empty but Node provides `__filename`; in the ESM
// bundle it's the reverse. Pick whichever anchor exists so `createRequire`
// resolves `../index.js` relative to the compiled file in `dist/`.
declare const __filename: string | undefined;
const anchor = typeof __filename !== "undefined" ? __filename : import.meta.url;
const require = createRequire(anchor);
export const native: NativeBindings = require("../index.js") as NativeBindings;
